import React, { useState, useRef } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { ChevronDown, HelpCircle, MessageSquare, Search, BookOpen, Shield, CreditCard, Zap } from 'lucide-react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Common/Navbar'
import Footer from '../components/Common/Footer'

const B = 'https://pixner.net/bankio/main/assets/images'

const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  )
}

const categories = [
  {
    label: 'Getting Started',
    items: [
      { q: 'How do I create an A1XPAY account?', a: 'Click "Open Account", fill in your business details, upload KYC documents (PAN, GST, bank statement), and submit. Most accounts are activated within 24 business hours.' },
      { q: 'What documents are required for KYC?', a: 'You need: PAN card, Aadhaar or passport, business registration certificate, cancelled cheque, and GST certificate (if applicable).' },
      { q: 'Is there a setup fee?', a: 'No setup fee. Your account is completely free to create. You only pay transaction fees as you earn.' },
      { q: 'How long does onboarding take?', a: 'Most accounts go live within 24–48 hours after KYC documents are submitted. Enterprise accounts may take 3–5 business days.' },
    ]
  },
  {
    label: 'Payments & Integration',
    items: [
      { q: 'What payment methods does A1XPAY support?', a: 'UPI (all apps), debit/credit cards (Visa, Mastercard, RuPay), net banking (50+ banks), wallets (Paytm, PhonePe, etc.), and EMI options.' },
      { q: 'Do you provide an API?', a: 'Yes — REST API with comprehensive documentation. SDKs are available for PHP, Node.js, Python, and React Native. A sandbox environment is included for testing.' },
      { q: 'Can I create payment links without a website?', a: 'Absolutely. Create a payment link from your dashboard in under 60 seconds and share it on WhatsApp, SMS, or email.' },
      { q: 'Is A1XPAY mobile app available?', a: 'A web-based mobile dashboard is available. Native iOS and Android apps are on the roadmap for Q1 2025.' },
    ]
  },
  {
    label: 'Pricing & Settlements',
    items: [
      { q: 'What are the transaction charges?', a: 'UPI: 0% (above ₹2,000), ₹3 flat fee below ₹2,000. Cards: 0.4%–2% depending on card type and volume. See our Pricing page for the full MDR table.' },
      { q: 'When do I receive my settlements?', a: 'Standard settlement is T+2 (two business days after transaction). T+1 settlement is available for eligible merchants on the Business and Enterprise plans.' },
      { q: 'Are there any hidden charges?', a: 'No. Our pricing page lists every charge clearly. You will never see an unexpected deduction on your settlement.' },
      { q: 'Is there a minimum monthly fee?', a: 'The Starter plan has no monthly fee. Business and Enterprise plans have a monthly platform fee — see our Pricing page.' },
    ]
  },
  {
    label: 'Security & Compliance',
    items: [
      { q: 'Is A1XPAY secure?', a: 'Yes. A1XPAY follows industry-standard security practices. Your customers\' card data never touches your servers and all transactions are encrypted.' },
      { q: 'How does A1XPAY protect against fraud?', a: 'We use real-time fraud detection, velocity checks, IP fingerprinting, and 3D Secure authentication for card payments to minimize chargebacks.' },
      { q: 'Is my data stored in India?', a: 'Yes. All data is stored on servers in India in compliance with RBI data localisation guidelines.' },
    ]
  },
  {
    label: 'Support',
    items: [
      { q: 'How do I reach support?', a: 'Phone: +91 99103 73510 (Mon–Sat, 10 AM–7 PM IST). Email: support@a1xpay.com. Dashboard chat available during business hours.' },
      { q: 'Do you offer dedicated account managers?', a: 'Yes, Business and Enterprise plan customers get a dedicated account manager and priority support with 4-hour SLA.' },
    ]
  },
]

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={`fq-item${open ? ' fq-open' : ''}`}>
      <button className="fq-q" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.22 }}>
          <ChevronDown size={18} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div className="fq-a-wrap"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}>
            <p className="fq-a">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function FAQ() {
  return (
    <>
      <style>{css}</style>
      <div className="fq-root">
        <Navbar />

        {/* Hero */}
        <section className="fq-hero">
          <div className="fq-container fq-hero-grid">
            <motion.div className="fq-hero-text"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
              <div className="fq-hero-circle"><HelpCircle size={28} color="#fff" /></div>
              <span className="fq-sub fq-green">Help Center</span>
              <h1 className="fq-hero-h1">Frequently Asked<br />Questions</h1>
              <p className="fq-hero-p">
                Get answers to all questions about A1XPAY — from getting started to advanced integrations.
              </p>
            </motion.div>
            <motion.div className="fq-hero-visual"
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
              <div className="fq-hv-card">
                <div className="fq-hv-search">
                  <Search size={16} color="#94A3B8" />
                  <span className="fq-hv-search-text">Search your question...</span>
                </div>
                <div className="fq-hv-cats">
                  {[{ icon: <Zap size={15}/>, l: 'Getting Started', n: '4 articles' },
                    { icon: <CreditCard size={15}/>, l: 'Payments & API', n: '4 articles' },
                    { icon: <Shield size={15}/>, l: 'Security', n: '3 articles' },
                    { icon: <BookOpen size={15}/>, l: 'Pricing & Billing', n: '4 articles' },
                    { icon: <MessageSquare size={15}/>, l: 'Support', n: '2 articles' }].map(c => (
                    <div key={c.l} className="fq-hv-cat">
                      <div className="fq-hv-cat-icon">{c.icon}</div>
                      <div className="fq-hv-cat-text">
                        <div className="fq-hv-cat-name">{c.l}</div>
                        <div className="fq-hv-cat-n">{c.n}</div>
                      </div>
                      <ChevronDown size={14} color="#94A3B8" style={{transform:'rotate(-90deg)'}} />
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ Categories */}
        <section className="fq-section fq-bg-white">
          <div className="fq-container">
            {categories.map((cat, ci) => (
              <FadeUp key={cat.label} delay={ci * 0.05}>
                <div className="fq-category">
                  <h2 className="fq-cat-title">{cat.label}</h2>
                  {cat.items.map((item) => <FaqItem key={item.q} q={item.q} a={item.a} />)}
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        {/* Stats + illustration section */}
        <section className="fq-stats-section">
          <div className="fq-container fq-stats-grid">
            <FadeUp delay={0.1} className="fq-stats-img">
              <motion.img src={`${B}/counter-Illus.png`} alt="Support stats"
                whileHover={{ scale: 1.03 }} transition={{ type: 'spring', stiffness: 180 }} />
            </FadeUp>
            <FadeUp className="fq-stats-text">
              <span className="fq-sub">Why choose A1XPAY support</span>
              <h2 className="fq-stats-h2">Fast answers, not chatbots</h2>
              <p className="fq-stats-p">Our support team comprises real payment experts, not generic call-center agents. We understand your integration, your business model, and your urgency.</p>
              <div className="fq-stats-row">
                {[['&lt;2hr', 'Avg response'], ['98%', 'CSAT score'], ['24/7', 'Ticket portal'], ['Mon–Sat', 'Phone support']].map(([v, l]) => (
                  <div key={l} className="fq-stat-item">
                    <div className="fq-stat-val" dangerouslySetInnerHTML={{__html: v}} />
                    <div className="fq-stat-lbl">{l}</div>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </section>

        {/* CTA */}
        <section className="fq-cta">
          <div className="fq-container fq-cta-flex">
            <FadeUp className="fq-cta-left">
              <span className="fq-sub fq-white">Still have questions?</span>
              <h2 className="fq-cta-h2">Our team is here to help</h2>
              <p className="fq-cta-p">Can't find what you're looking for? Reach out to our support team and we'll get back to you within 2 hours.</p>
              <div className="fq-cta-btns">
                <Link to="/contact-us" className="fq-btn-white">Contact Support</Link>
              </div>
            </FadeUp>
            <motion.div className="fq-cta-illu"
              initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}>
              <img src={`${B}/get-start.png`} alt="Get started" />
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;800;900&display=swap');
.fq-root { font-family: 'Jost', sans-serif; background: #fff; }
.fq-container { max-width: 860px; margin: 0 auto; padding: 0 1.5rem; }
.fq-hero .fq-container { max-width: 1200px; }
.fq-hero { background: linear-gradient(135deg, #f5faff 0%, #eef3ff 100%); padding: 100px 0 80px; }
.fq-hero-grid { max-width: 1200px; display: grid; grid-template-columns: 1fr; gap: 3rem; align-items: center; }
@media (min-width:1024px) { .fq-hero-grid { grid-template-columns: 1fr 1fr; gap: 4rem; } }
.fq-hero-circle { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg,#1A4DBE,#06B6D4); display: flex; align-items: center; justify-content: center; margin-bottom: 1.25rem; box-shadow: 0 8px 24px rgba(26,77,190,0.3); }
.fq-sub { font-size: 0.875rem; font-weight: 700; color: #1A4DBE; margin-bottom: 0.875rem; display: block; text-transform: uppercase; letter-spacing: 0.08em; }
.fq-green { color: #1A4DBE; }
.fq-white { color: rgba(255,255,255,0.85); }
.fq-hero-h1 { font-size: clamp(2rem, 4.5vw, 3.25rem); font-weight: 800; color: #0A1023; letter-spacing: -0.04em; line-height: 1.15; margin-bottom: 1.25rem; }
.fq-hero-p { font-size: 1.0625rem; color: #2C3655; line-height: 1.75; }
/* Hero visual */
.fq-hero-visual { display: none; }
@media (min-width:1024px) { .fq-hero-visual { display: block; } }
.fq-hv-card { background: #fff; border-radius: 1.25rem; padding: 1.5rem; box-shadow: 0 20px 60px rgba(26,77,190,0.15); border: 1px solid #E8EBF4; }
.fq-hv-search { display: flex; align-items: center; gap: 0.75rem; background: #f5faff; border: 1.5px solid #E8EBF4; border-radius: 0.75rem; padding: 0.75rem 1rem; margin-bottom: 1.25rem; }
.fq-hv-search-text { font-size: 0.875rem; color: #94A3B8; }
.fq-hv-cats { display: flex; flex-direction: column; gap: 0.5rem; }
.fq-hv-cat { display: flex; align-items: center; gap: 0.875rem; padding: 0.75rem; background: #f8faff; border-radius: 0.625rem; border: 1px solid #E8EBF4; cursor: default; transition: border-color 0.2s; }
.fq-hv-cat:hover { border-color: #B0BAF1; }
.fq-hv-cat-icon { width: 30px; height: 30px; background: linear-gradient(135deg,#1A4DBE,#06B6D4); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
.fq-hv-cat-text { flex: 1; }
.fq-hv-cat-name { font-size: 0.8125rem; font-weight: 700; color: #0A1023; }
.fq-hv-cat-n { font-size: 0.75rem; color: #94A3B8; }
.fq-section { padding: 80px 0; }
.fq-bg-white { background: #fff; }
.fq-category { margin-bottom: 3rem; }
.fq-cat-title { font-size: 1.375rem; font-weight: 700; color: #0A1023; margin-bottom: 1.25rem; padding-bottom: 0.75rem; border-bottom: 2px solid #EEF3FF; }
.fq-item { background: #fff; border: 1px solid #E8EBF4; border-radius: 0.75rem; margin-bottom: 0.625rem; overflow: hidden; transition: border-color 0.2s; }
.fq-open { border-color: #B0BAF1; }
.fq-q { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1rem 1.25rem; background: none; border: none; cursor: pointer; text-align: left; font-size: 0.9375rem; font-weight: 600; color: #0A1023; font-family: 'Jost', sans-serif; }
.fq-a-wrap { overflow: hidden; }
.fq-a { padding: 0 1.25rem 1rem; font-size: 0.9rem; color: #2C3655; line-height: 1.7; margin: 0; font-family: 'Jost', sans-serif; }
/* Stats section */
.fq-stats-section { padding: 80px 0; background: #f5faff; }
.fq-stats-section .fq-container { max-width: 1200px; }
.fq-stats-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; align-items: center; }
@media (min-width:1024px) { .fq-stats-grid { grid-template-columns: 1fr 1fr; gap: 5rem; } }
.fq-stats-img { display: flex; align-items: center; justify-content: center; }
.fq-stats-img img { max-width: 100%; max-height: 380px; object-fit: contain; }
.fq-stats-h2 { font-size: clamp(1.75rem, 3.5vw, 2.5rem); font-weight: 700; color: #0A1023; letter-spacing: -0.03em; margin-bottom: 1rem; }
.fq-stats-p { font-size: 1rem; color: #2C3655; line-height: 1.75; margin-bottom: 1.75rem; }
.fq-stats-row { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; }
.fq-stat-item { background: #fff; border: 1.5px solid #E8EBF4; border-radius: 0.875rem; padding: 1rem; text-align: center; }
.fq-stat-val { font-size: 1.625rem; font-weight: 800; color: #1A4DBE; letter-spacing: -0.03em; }
.fq-stat-lbl { font-size: 0.8125rem; color: #64748B; font-weight: 500; margin-top: 0.2rem; }
/* CTA */
.fq-cta { background: #1A4DBE; padding: 80px 0; position: relative; overflow: hidden; }
.fq-cta::before { content: ''; position: absolute; width: 400px; height: 400px; border-radius: 50%; background: rgba(255,255,255,0.05); top: -150px; right: -80px; }
.fq-cta-flex { position: relative; z-index: 2; display: flex; align-items: center; justify-content: space-between; gap: 2rem; flex-wrap: wrap; }
.fq-cta-left { flex: 1; min-width: 280px; }
.fq-cta-h2 { font-size: clamp(1.875rem, 4vw, 2.75rem); font-weight: 800; color: #fff; letter-spacing: -0.04em; margin-bottom: 0.75rem; }
.fq-cta-p { font-size: 1rem; color: rgba(255,255,255,0.8); max-width: 460px; margin: 0 0 2rem; line-height: 1.65; }
.fq-cta-btns { display: flex; gap: 1rem; flex-wrap: wrap; }
.fq-cta-illu { display: none; }
@media (min-width:900px) { .fq-cta-illu { display: block; width: 200px; flex-shrink: 0; } }
.fq-cta-illu img { max-width: 100%; filter: brightness(1.1) drop-shadow(0 8px 24px rgba(0,0,0,0.2)); }
.fq-btn-white { display: inline-flex; align-items: center; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #1A4DBE; background: #fff; border: 1.5px solid #fff; border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; }
.fq-btn-white:hover { background: #f0f4ff; }
`
